// Penghubung data kasir web -> tata letak struk (strukLayout.js).
import { buatBarisStruk, buatBarisTiket, formatWaktuStruk, opsiDariKonfigurasi, opsiTiketDariKonfigurasi } from './strukLayout';
import { alamatUlasanPendek } from '../utils/ulasanPendek';
import { waktuCabang } from '../utils/waktuCabang';
import { barisBagi } from '../utils/bagiBayar';
import { barisBuktiPiutang } from '../utils/uangMuka';
import { labelAntrian } from '../utils/antrian';

const labelMetode = (m) => {
  if (/^qris$/i.test(String(m || ''))) return 'QRIS';
  if (m === 'DP') return 'Uang Muka (DP)';
  return String(m || '-');
};

/**
 * Baris struk untuk satu nota kasir web.
 * @param {object} p
 * @param {object} p.receipt      { items:[{name,price,quantity,catatan,selectedOptions}], total, subtotal, diskon, promoNama,
 *                                  ppnPersen, ppnNominal, paymentMethod, paymentLines, nomorAntrian, namaCustomer, noted,
 *                                  customerCash, createdAt, orderId, meja }
 * @param {object|null} p.cabangConfig  konfigurasi cabang (noTelp, wifi, wifiNama, teksA, teksB); null = tanpa konfigurasi
 * @param {string} p.namaCabang
 * @param {string} p.kasir
 * @param {string|null} p.logoUrl
 * @param {object} [p.opsi]  { kolom, tampil, catatanKaki }; bawaan dari pengaturan struk di cabangConfig (lebar, sakelar, catatan kaki)
 */
export const barisStrukKasir = ({ receipt, cabangConfig, namaCabang, kasir, logoUrl, opsi = opsiDariKonfigurasi(cabangConfig) }) => {
  const r = receipt || {};
  const cabang = cabangConfig
    ? {
        nama: namaCabang,
        noTelp: cabangConfig.noTelp,
        wifiNama: cabangConfig.wifiNama,
        wifi: cabangConfig.wifi,
        teksA: cabangConfig.teksA,
        teksB: cabangConfig.teksB,
        logoUrl: logoUrl || '',
        ulasanUrl: alamatUlasanPendek(cabangConfig),
      }
    : null;
  const trx = {
    namaCabang,
    metodeBayar: labelMetode(r.paymentMethod),
    customer: r.namaCustomer || 'Walk-in Customer',
    kasir: kasir || 'Kasir',
    waktu: formatWaktuStruk(waktuCabang(r.createdAt || Date.now())),
    orderId: r.orderId || '-',
    meja: r.meja || '',
    nomorAntrian: r.nomorAntrian || '',
    noted: r.noted || '',
    items: (r.items || []).map((it) => {
      const opsi = (it.selectedOptions || []).map((o) => o.opsinama).filter(Boolean);
      return {
        nama: opsi.length ? `${it.name} (${opsi.join(', ')})` : it.name,
        harga: Number(it.price) || 0,
        qty: Number(it.quantity) || 0,
        catatan: it.catatan || '',
      };
    }),
    total: Number(r.total) || 0,
    subtotal: r.subtotal === undefined || r.subtotal === null ? undefined : Number(r.subtotal),
    diskonPromo: Number(r.diskon) || 0,
    namaPromo: r.promoNama || '',
    ppnPersen: r.ppnPersen,
    ppnNominal: Number(r.ppnNominal) || 0,
    uangCustomer: Number(r.customerCash) || 0,
    barisBagi: r.paymentMethod === 'Split' || r.paymentMethod === 'DP' ? r.paymentLines || [] : [],
  };
  return buatBarisStruk({ cabang, trx, ...opsi });
};

const upload = (base, f) => (f ? `${base}/uploads/${f}` : null);

/** Konfigurasi struk cabang (noTelp, wifi, teksA/B, logo) dari server; null bila gagal -> struk tanpa blok konfigurasi. */
export const ambilKonfigStruk = async (axios, baseUrl, cabangUuid) => {
  try {
    const r = await axios.get(`${baseUrl}/getkonfigurasicabang`, { params: cabangUuid ? { cabangUuid } : {}, withCredentials: true });
    const k = r.data?.data || r.data || null;
    if (!k) return { cabangConfig: null, logoUrl: null };
    return {
      cabangConfig: k,
      logoUrl: k.logoTampilStruk !== false && k.logo ? upload(baseUrl, k.logo) : null,
    };
  } catch {
    return { cabangConfig: null, logoUrl: null };
  }
};

/**
 * Baris struk untuk transaksi dari server (cetak ulang riwayat / detail pesanan).
 * @param {object} trx  Transaksi + TransaksiDetails[{Barang,jumlahbarang,harga,total,catatan}] + User{username,nama,Cabang}
 */
export const barisStrukDariTransaksi = ({ trx, cabangConfig, logoUrl }) => {
  const t = trx || {};
  const bagian = Array.isArray(t.pembayaranBagi) ? t.pembayaranBagi : null;
  const total = Number(t.totaljual) || 0;
  // Transaksi dengan uang muka: rincian DP, total dibayar, sisa tagihan (atau LUNAS).
  const piutang = !!t.adaPiutang && bagian && bagian.length > 0;
  const receipt = {
    items: (t.TransaksiDetails || []).map((d) => ({
      name: d.Barang?.namabarang || 'Item',
      price: Number(d.harga) || 0,
      quantity: Number(d.jumlahbarang) || 0,
      catatan: d.catatan || '',
    })),
    total,
    subtotal: t.subtotal === undefined || t.subtotal === null ? undefined : Number(t.subtotal),
    diskon: Number(t.diskonTotal) || 0,
    promoNama: t.promoNama || '',
    ppnPersen: t.ppnPersen,
    ppnNominal: Number(t.ppnNominal) || 0,
    paymentMethod: piutang ? 'DP' : bagian && bagian.length > 0 ? 'Split' : labelMetodeDasar(t.pembayaran),
    paymentLines: piutang
      ? barisBuktiPiutang({ uangMuka: bagian, pembayaran: [], dibayar: total - (Number(t.sisaTagihan) || 0), sisaTagihan: t.sisaTagihan, jatuhTempo: t.jatuhTempo })
      : bagian && bagian.length > 0 ? barisBagi(bagian, total) : [],
    nomorAntrian: labelAntrian(t),
    namaCustomer: t.namaCustomer || 'Walk-in Customer',
    noted: t.noted || '',
    customerCash: Number(t.uangCustomer) > 0 ? Number(t.uangCustomer) : total,
    createdAt: t.createdAt || t.tanggal,
    orderId: t.order_id,
    meja: t.Table?.name || t.Table?.nomeja || t.takeawayName || '',
  };
  return barisStrukKasir({
    receipt,
    cabangConfig,
    namaCabang: t.User?.Cabang?.namacabang || 'Kasir',
    kasir: t.User?.nama || t.User?.username || 'Kasir',
    logoUrl,
  });
};

function labelMetodeDasar(p) {
  const v = String(p || '').toLowerCase();
  if (v === 'cash') return 'Cash';
  if (v === 'qris' || v === 'qris_statis') return 'QRIS';
  if (v === 'noncash') return 'Non Cash';
  return p || '-';
}

/**
 * Tiket dapur/bar (tanpa harga) dari transaksi server, mis. tombol cetak di halaman Dapur.
 * @param {object} p
 * @param {object} p.trx  Transaksi + TransaksiDetails[{Barang,jumlahbarang,catatan,TransaksiDetailOpsis[{opsinama}]}]
 * @param {'dapur'|'bar'} [p.jenis]
 * @param {object|null} p.cabangConfig  konfigurasi cabang (memuat pengaturan tiket)
 * @param {string} [p.namaCabang]
 * @param {string} [p.kasir]
 * @param {string} [p.meja]   meja / nama takeaway (bila tidak ada di transaksi)
 */
export const barisTiketDariTransaksi = ({ trx, jenis = 'dapur', cabangConfig, namaCabang = '', kasir = '', meja = '' }) => {
  const t = trx || {};
  // Pemisahan menu per printer (menu Struk > Tiket Bar): kategori di daftar BAR dicetak di BAR, sisanya di DAPUR.
  // Halaman web tidak tahu printer yang terpasang di HP, jadi tidak ada pengalihan: tiket dapur memuat item non-BAR saja.
  const pisah = cabangConfig && cabangConfig.tiketPisah;
  const kategoriBar = new Set(pisah && pisah.aktif && Array.isArray(pisah.bar) ? pisah.bar : []);
  const itemBagian = (d) => {
    if (!pisah || !pisah.aktif) return true;
    const keBar = kategoriBar.has(d.Barang && d.Barang.kategoriuuid);
    return jenis === 'bar' ? keBar : !keBar;
  };
  const items = (t.TransaksiDetails || []).filter(itemBagian).map((d) => {
    const opsi = (d.TransaksiDetailOpsis || []).map((o) => o.opsinama).filter(Boolean);
    const nama = d.Barang?.namabarang || 'Item';
    return { nama: opsi.length ? `${nama} (${opsi.join(', ')})` : nama, qty: Number(d.jumlahbarang) || 0, catatan: d.catatan || '' };
  });
  const baris = buatBarisTiket({
    trx: {
      namaCabang: namaCabang || t.User?.Cabang?.namacabang || '',
      nomorAntrian: labelAntrian(t),
      meja: meja || t.Table?.name || t.takeawayName || '',
      customer: t.namaCustomer || '',
      kasir: kasir || t.User?.nama || '',
      waktu: formatWaktuStruk(waktuCabang(t.createdAt || Date.now())),
      orderId: t.order_id || '',
      noted: t.noted || '',
      items,
    },
    ...opsiTiketDariKonfigurasi(cabangConfig, jenis),
  });
  baris.jumlahItem = items.length; // 0 = tidak ada item untuk tiket ini (mis. pesanan minuman saja untuk tiket dapur)
  return baris;
};
